import SidebarComponent from 'components/layouts/sidebar';
import {useSelector} from 'react-redux';
import {orgSelector, userSelector} from '../redux/selectors';
import {Spinner, Table} from 'flowbite-react';
import {useTranslation} from 'react-i18next';
import Header from 'components/header';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PieChart from 'components/commonComponent/chart/pieChart';
import ReportTable from 'components/commonComponent/chart/reportTable';
import { useQuery } from '@tanstack/react-query';
import { defaultpageCount, permissionKeys, sortByTypeType } from 'utils/constants';
import { getDashboardKpis, getItemSaleFilter, getItemShrinkageFilter, getLowHighStockFilter } from 'api/dashboardApi';
import { checkOrgAdmin, checkPermission, getUserOrg } from 'utils/utils';
import NoticeBanner from 'components/commonComponent/banner/noticeBanner';
import SpinnerCustom from 'components/spinnerCustom';
import { getRegionByFilter } from 'api/regionApi';
import { getLocationByFilter } from 'api/locationApi';
import Select from 'components/select';

type SelectOption = { label: string; value: string; rawName?: string };
type DateRangeOption = 7 | 14 | 30 | 60 | 90;

const DATE_RANGE_OPTIONS: DateRangeOption[] = [90, 60, 30, 14, 7];

const DashboardPage = () => {
    const [t] = useTranslation();
    const {userInfo} = useSelector(userSelector);
    const building = require('../../src/assets/image/svg/building.svg').default;
    const {organizationId} = useSelector(orgSelector);
    const [lowStocks, setLowStocks] = useState([]);
    const [hasMoreLow, setHasMoreLow] = useState(true);
    const [currentPageLowStock, setCurrentPageLowStock] = useState(0);
    const [currentPageHighStock, setCurrentPageHighStock] = useState(0);
    const [highStocks, setHighStocks] = useState([]);
    const [hasMoreHigh, setHasMoreHigh] = useState(true);
    const [locationData, setLocationData] = useState<any>([]);
    const [categoryData, setCategoryData] = useState<any>([]);
    const [reportShrinkageData, setReportShrinkageData] = useState<any>([]);
    const [pageCount] = useState(defaultpageCount);
    const [selectedRegionOption, setSelectedRegionOption] = useState<SelectOption | null>(null);
    const [selectedLocationOption, setSelectedLocationOption] = useState<SelectOption | null>(null);
    const [dateRangeDays, setDateRangeDays] = useState<DateRangeOption>(30);
    const [regionOptions, setRegionOptions] = useState<SelectOption[]>([]);
    const [locationOptions, setLocationOptions] = useState<SelectOption[]>([]);
    const [allLocations, setAllLocations] = useState<any[]>([]);
    const [regionChildrenMap, setRegionChildrenMap] = useState<Record<string, string[]>>({});

    const {PLATFORM_ADMIN} = permissionKeys;
    const isAdmin = checkPermission(userInfo, "Organization", [PLATFORM_ADMIN], organizationId);
    const isOrgAdmin = checkOrgAdmin(userInfo, organizationId, isAdmin);
    const getRegions = useMemo(() => {
        const userOrg = userInfo?.organizations?.find((o: any) => o.id === organizationId);
        const orgRegions = userOrg?.regions;
        let regionIds = '';
        if (orgRegions) {
            for (let index = 0; index < orgRegions.length; index++) {
                const region = orgRegions[index];
                if (regionIds === '') {
                    regionIds = region.id;
                } else {
                    regionIds = `${regionIds},${region.id}`;
                }
            }
        }
        return regionIds;
    }, [userInfo, organizationId]);
    const scopedUserOrgId = useMemo(
        () => (isOrgAdmin === true ? null : getUserOrg(userInfo, organizationId)?.userOrganizationId),
        [isOrgAdmin, userInfo, organizationId],
    );
    const scopedRegionIds = useMemo(
        () => (isOrgAdmin === true ? '' : getRegions),
        [isOrgAdmin, getRegions],
    );

    const getRegionAndSubRegionIds = useCallback(
        (regionId: string) => {
            const visited = new Set<string>();
            const queue = [regionId];
            while (queue.length > 0) {
                const current = queue.shift() as string;
                if (visited.has(current)) continue;
                visited.add(current);
                const children = regionChildrenMap[current] || [];
                for (let index = 0; index < children.length; index++) {
                    queue.push(children[index]);
                }
            }
            return Array.from(visited);
        },
        [regionChildrenMap],
    );

    const getLocationOptionLabel = useCallback((location: any) => {
        const name = String(location?.name || '').trim();
        const regionName = String(location?.region?.name || location?.regionName || '').trim();
        const clubCode = String(location?.clubCode || '').trim();
        const suffix = regionName || clubCode;
        return suffix ? `${name} (${suffix})` : name;
    }, []);

    const effectiveRegionIds = useMemo(() => {
        if (!selectedRegionOption?.value) return scopedRegionIds;
        return getRegionAndSubRegionIds(selectedRegionOption.value).join(',');
    }, [selectedRegionOption, scopedRegionIds, getRegionAndSubRegionIds]);

    const effectiveLocationId = useMemo(
        () => selectedLocationOption?.value || '',
        [selectedLocationOption],
    );

    const {data: lowStockData, isFetching: isFetchingLow} = useQuery(
        ['getLowStockFilter', organizationId, currentPageLowStock, pageCount, scopedUserOrgId, effectiveRegionIds, effectiveLocationId, dateRangeDays],
        () => getLowHighStockFilter({
            page: currentPageLowStock,
            limit: pageCount,
            organizationId: organizationId,
            stockType: 'LowStock',
            userOrgId: scopedUserOrgId,
            regionIds: effectiveRegionIds,
            locationId: effectiveLocationId,
            dateRangeDays,
        }),
        {
            staleTime: 60 * 1000,
            keepPreviousData: true,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
        },
    );

    const {data: kpiData, isLoading: isLoadingKpis} = useQuery(
        ['getDashboardKpis', organizationId, scopedUserOrgId, effectiveRegionIds, effectiveLocationId, dateRangeDays],
        () => getDashboardKpis({
            organizationId: organizationId,
            userOrgId: scopedUserOrgId,
            regionIds: effectiveRegionIds,
            locationId: effectiveLocationId,
            dateRangeDays,
        }),
        {
            staleTime: 60 * 1000,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
        },
    );

    const {data: highStockData, isFetching: isFetchingHigh} = useQuery(
        ['getHighStockFilter', organizationId, currentPageHighStock, pageCount, scopedUserOrgId, effectiveRegionIds, effectiveLocationId, dateRangeDays],
        () => getLowHighStockFilter({
            page: currentPageHighStock,
            limit: pageCount,
            organizationId: organizationId,
            stockType: 'HighStock',
            userOrgId: scopedUserOrgId,
            regionIds: effectiveRegionIds,
            locationId: effectiveLocationId,
            dateRangeDays,
        }),
        {
            staleTime: 60 * 1000,
            keepPreviousData: true,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
        },
    );

    useEffect(() => {
        setCurrentPageHighStock(0);
        setHighStocks([]);
        setHasMoreHigh(true);
        setCurrentPageLowStock(0);
        setLowStocks([]);
        setHasMoreLow(true);
        setDateRangeDays(30);
        setSelectedRegionOption(null);
        setSelectedLocationOption(null);
    }, [organizationId]);

    useEffect(() => {
        if (!organizationId) return;
        const pageSize = 200;
        let isMounted = true;

        const loadRegions = async () => {
            const first = await getRegionByFilter({
                page: 0,
                limit: pageSize,
                organizationId,
                userOrgId: scopedUserOrgId,
                regionIds: scopedRegionIds,
                isActive: true,
                sortByType: sortByTypeType.DESC,
            });
            const totalCount = first?.data?.totalEntities ?? 0;
            const firstEntities = first?.data?.entities ?? [];
            const pageCount = Math.ceil(totalCount / pageSize);
            const restPromises = [];
            for (let index = 1; index < pageCount; index++) {
                restPromises.push(
                    getRegionByFilter({
                        page: index,
                        limit: pageSize,
                        organizationId,
                        userOrgId: scopedUserOrgId,
                        regionIds: scopedRegionIds,
                        isActive: true,
                        sortByType: sortByTypeType.DESC,
                    }).then((response: any) => response?.data?.entities ?? []),
                );
            }
            const rest = await Promise.all(restPromises);
            const regions = firstEntities.concat(...rest);
            if (!isMounted) return;

            const childrenMap: Record<string, string[]> = {};
            for (let index = 0; index < regions.length; index++) {
                const region = regions[index];
                const parentId = region?.parentRegion?.id ?? region?.parentGroup?.id;
                if (parentId) {
                    if (!childrenMap[parentId]) childrenMap[parentId] = [];
                    childrenMap[parentId].push(region.id);
                }
            }

            setRegionChildrenMap(childrenMap);
            setRegionOptions(regions.map((region: any) => ({ value: region.id, label: region.name })));
        };

        loadRegions().catch(console.error);
        return () => {
            isMounted = false;
        };
    }, [organizationId, scopedUserOrgId, scopedRegionIds]);

    useEffect(() => {
        if (!organizationId) return;
        const pageSize = 200;
        let isMounted = true;

        const loadLocations = async () => {
            const locationRegionIds = selectedRegionOption?.value
                ? getRegionAndSubRegionIds(selectedRegionOption.value).join(',')
                : scopedRegionIds;
            const first = await getLocationByFilter({
                page: 0,
                limit: pageSize,
                organizationId,
                userOrgId: scopedUserOrgId,
                regionIds: locationRegionIds,
                isActive: true,
                sortByType: sortByTypeType.DESC,
            });
            const totalCount = first?.data?.totalEntities ?? 0;
            const firstEntities = first?.data?.entities ?? [];
            const pageCount = Math.ceil(totalCount / pageSize);
            const restPromises = [];
            for (let index = 1; index < pageCount; index++) {
                restPromises.push(
                    getLocationByFilter({
                        page: index,
                        limit: pageSize,
                        organizationId,
                        userOrgId: scopedUserOrgId,
                        regionIds: locationRegionIds,
                        isActive: true,
                        sortByType: sortByTypeType.DESC,
                    }).then((response: any) => response?.data?.entities ?? []),
                );
            }
            const rest = await Promise.all(restPromises);

            if (!isMounted) return;
            setAllLocations(firstEntities.concat(...rest));
        };

        loadLocations().catch(console.error);
        return () => {
            isMounted = false;
        };
    }, [organizationId, scopedUserOrgId, scopedRegionIds, selectedRegionOption, getRegionAndSubRegionIds]);

    useEffect(() => {
        const options = allLocations.map((location: any) => ({
            value: location.id,
            label: getLocationOptionLabel(location),
            rawName: location.name,
        }));
        setLocationOptions(options);

        if (selectedLocationOption?.value && !options.find((option) => option.value === selectedLocationOption.value)) {
            setSelectedLocationOption(null);
        }
    }, [allLocations, selectedLocationOption, getLocationOptionLabel]);


    const {data: pieData, isLoading: isLoadingPieData} = useQuery(
        ['getItemSaleFilter', organizationId, scopedUserOrgId, effectiveRegionIds, effectiveLocationId, dateRangeDays],
        () => getItemSaleFilter({
            page: 0,
            limit: pageCount,
            organizationId: organizationId,
            userOrgId: scopedUserOrgId,
            regionIds: effectiveRegionIds,
            locationId: effectiveLocationId,
            dateRangeDays,
        }),
        {
            staleTime: 60 * 1000,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
        },
    );

    const {data: shrinkageData, isLoading: isLoadingShrinkage} = useQuery(
        ['getItemShrinkageFilter', organizationId, scopedUserOrgId, effectiveRegionIds, effectiveLocationId, dateRangeDays],
        () => getItemShrinkageFilter({
            page: 0,
            limit: pageCount,
            organizationId: organizationId,
            userOrgId: scopedUserOrgId,
            regionIds: effectiveRegionIds,
            locationId: effectiveLocationId,
            dateRangeDays,
        }),
        {
            staleTime: 60 * 1000,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
        },
    );

    useEffect(() => {
        if (pieData) {
            const rawLocationData = pieData.data.locationData || { labels: [], values: [] };
            let nextLocationData = rawLocationData;
            if (selectedLocationOption?.rawName || selectedLocationOption?.label) {
                const selectedLocationName = selectedLocationOption?.rawName || selectedLocationOption?.label;
                const matchedIndex = rawLocationData.labels?.findIndex(
                    (label: string) => label === selectedLocationName,
                );
                if (matchedIndex >= 0) {
                    nextLocationData = {
                        labels: [rawLocationData.labels[matchedIndex]],
                        values: [rawLocationData.values[matchedIndex]],
                    };
                } else {
                    nextLocationData = { labels: [], values: [] };
                }
            }
            setLocationData(nextLocationData);
            // Normalize categoryData labels: replace null with "None"
            const normalizedCategoryData = {
                ...pieData.data.categoryData,
                labels: pieData.data.categoryData?.labels?.map((label: string | null) => label ?? "None") ?? []
            };
            setCategoryData(normalizedCategoryData);
            setLegend(nextLocationData?.labels);
            setColors(makeColors(nextLocationData?.labels?.length || 0));
        }
        // eslint-disable-next-line
    }, [pieData, organizationId, selectedLocationOption])

    useEffect(() => {
        setCurrentPageHighStock(0);
        setHighStocks([]);
        setHasMoreHigh(true);
        setCurrentPageLowStock(0);
        setLowStocks([]);
        setHasMoreLow(true);
    }, [effectiveRegionIds, effectiveLocationId, dateRangeDays]);

    useEffect(() => {
        if (shrinkageData) {
            if (effectiveLocationId) {
                setReportShrinkageData(
                    (shrinkageData.data || []).filter((row: any) => row.id === effectiveLocationId),
                );
            } else {
                setReportShrinkageData(shrinkageData.data);
            }
        }
        // eslint-disable-next-line
    }, [shrinkageData, effectiveLocationId])

    const makeColors = useCallback((n: number) =>
        Array.from({ length: n }, (_, i) => `hsl(${Math.round((360 / n) * i)}, 45%, 55%)`), []);

    const [mode, setMode] = useState<'location' | 'category'>('location');
    const [legend, setLegend] = useState<string[]>(locationData?.labels);
    const [colors, setColors] = useState<string[]>(makeColors(locationData?.labels?.length));
    const [updatePie, setUpdatePie] = useState<
        ((labels: string[], values: number[], colors?: string[]) => void) | null
    >(null);

    useEffect(() => {
        if (effectiveLocationId && mode === 'category') {
            setMode('location');
        }
    }, [effectiveLocationId, mode]);

    useEffect(() => {
        if (!updatePie) return;
        const d = mode === 'location' ? locationData : categoryData;
        if (!d?.labels?.length || !d?.values?.length) return;
    
        const cols = makeColors(d?.labels?.length);
        setColors(cols);
        setLegend(d?.labels);
        updatePie(d?.labels, d?.values, cols);
        // eslint-disable-next-line
    }, [mode, updatePie, locationData, categoryData]);

    useEffect(() => {
        if (!locationData?.values?.length) {
            setUpdatePie(null);
        }
    }, [locationData]);

    useEffect(() => {
        if (lowStockData) {
            let newItems = lowStockData.data.data ?? lowStockData.data.data ?? [];
            if (effectiveLocationId) {
                newItems = newItems.filter((item: any) => item.locationId === effectiveLocationId);
            }

            if (newItems.length < defaultpageCount) setHasMoreLow(false);
        
            setLowStocks((prev) =>
            {
                if (currentPageLowStock === 0) {
                    return newItems
                } else {
                    const tempLow = removeDuplicates([...prev, ...newItems]);
                    return tempLow;
                }
            }
            );
        }
        // eslint-disable-next-line
    }, [lowStockData, currentPageLowStock, effectiveLocationId])

    useEffect(() => {
        if (highStockData) {
            let newItems = highStockData.data.data ?? highStockData.data.data ?? [];
            if (effectiveLocationId) {
                newItems = newItems.filter((item: any) => item.locationId === effectiveLocationId);
            }

            if (newItems.length < defaultpageCount) setHasMoreHigh(false);
        
            setHighStocks((prev) =>
                {
                    if(currentPageHighStock === 0) {
                        return newItems
                    }else {
                        const tempHigh = removeDuplicates([...prev, ...newItems]);
                        return tempHigh;
                    }
                }
            );
        }
        // eslint-disable-next-line
    }, [highStockData, currentPageHighStock, effectiveLocationId])

    /* ------------------ infinite scroll ------------------ */
    const containerHighRef = useRef<HTMLDivElement>(null);
    const sentinelHighRef = useRef<HTMLDivElement>(null);
    const containerLowRef = useRef<HTMLDivElement>(null);
    const sentinelLowRef = useRef<HTMLDivElement>(null);

    const loadMoreHigh = useCallback(() => {

        if (isFetchingHigh || !hasMoreHigh) return;
        setCurrentPageHighStock((p) => p + 1);
    }, [isFetchingHigh, hasMoreHigh]);

    const loadMoreLow = useCallback(() => {

        if (isFetchingLow || !hasMoreLow) return;
        setCurrentPageLowStock((p) => p + 1);
    }, [isFetchingLow, hasMoreLow]);

    useEffect(() => {
        const root = containerHighRef.current;
        const sentinel = sentinelHighRef.current;
        if (!root || !sentinel) return;

        let lastCallTime = 0;
        const observer = new IntersectionObserver(
            (entries) => {
            const e = entries[0];
            const now = Date.now();
            if (e.isIntersecting && now - lastCallTime > 200) {
                lastCallTime = now;
                loadMoreHigh();
            }
            },
            { root, threshold: 0.1, rootMargin: '50px' }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [loadMoreHigh]);

    useEffect(() => {
        const root = containerLowRef.current;
        const sentinel = sentinelLowRef.current;
        if (!root || !sentinel) return;

        let lastCallTime = 0;
        const observer = new IntersectionObserver(
            (entries) => {
            const e = entries[0];
            const now = Date.now();
            if (e.isIntersecting && now - lastCallTime > 200) {
                lastCallTime = now;
                loadMoreLow();
            }
            },
            { root, threshold: 0.1, rootMargin: '50px' }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [loadMoreLow]);

    const removeDuplicates = useCallback((items: any) => {
        const seen = new Set<string>();
        return items.filter((item: any) => {
          const key = `${item.locationId}-${item.itemId}`;
          if (seen.has(key)) {
            return false;
          }
          seen.add(key);
          return true;
        });
    }, [])

    const formatCurrency = useCallback((value?: number) => {
        const safeValue = Number.isFinite(Number(value)) ? Number(value) : 0;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 2,
        }).format(safeValue);
    }, []);

    if (!userInfo?.id) {
        return (
            <Spinner/>
        );
    }

    return (
        <>
            <NoticeBanner />
            <Header/>
            <div className="w-full overflow-hidden flex bg-white h-page relative">
                <SidebarComponent isDashboard={true}/>
                <div className="relative w-full flex flex-col main-content" style={{ willChange: 'scroll-position' }}>
                    {userInfo?.organizations.length === 0 
                    ? 
                    (
                        <div className="h-full pt-16">
                            <div className="mb-4 flex justify-center">
                                <img alt="MileMarker logo" src={building}/>
                            </div>
                            <p className="text-2xl font-semibold text-center mb-4">{t('dashboard.notification')}</p>
                            <p className="text-sm font-normal text-center	">{t('dashboard.note')}</p>
                        </div>
                    )
                    :
                    (
                        <>
                            <section className="bg-white rounded-xl shadow-sm px-4 py-3 mb-6">
                                <div className="grid grid-cols-1 lg:grid-cols-[220px_220px_1fr] gap-3 items-end">
                                    <div>
                                        <p className="text-xs font-semibold text-slate-500 mb-1">Region</p>
                                        <Select
                                            options={regionOptions}
                                            value={selectedRegionOption}
                                            onChange={(option: SelectOption | null) => setSelectedRegionOption(option)}
                                            placeholder="All Regions"
                                            isClearable
                                        />
                                    </div>
                                    <div>
                                        <p className="text-xs font-semibold text-slate-500 mb-1">Location</p>
                                        <Select
                                            options={locationOptions}
                                            value={selectedLocationOption}
                                            onChange={(option: SelectOption | null) => setSelectedLocationOption(option)}
                                            placeholder="All Locations"
                                            isClearable
                                        />
                                    </div>
                                    <div className="lg:justify-self-end flex items-center gap-2">
                                        <p className="text-xs font-semibold text-slate-500 whitespace-nowrap">Date Range</p>
                                        <div className="inline-flex items-center rounded border border-slate-300 overflow-hidden bg-white">
                                            
                                            {DATE_RANGE_OPTIONS.map((days) => {
                                                const active = dateRangeDays === days;
                                                return (
                                                    <button
                                                        key={days}
                                                        type="button"
                                                        onClick={() => setDateRangeDays(days)}
                                                        className={`px-2 py-2 text-[11px] border-l first:border-l-0 ${
                                                            active
                                                                ? 'bg-sky-500 text-white font-semibold border-sky-500'
                                                                : 'bg-white text-slate-700 border-slate-300'
                                                        }`}
                                                    >
                                                        {days} Days
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </section>
                            <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <div className="bg-white rounded-xl shadow-sm p-4">
                                    <p className="text-xs text-slate-500 font-medium mb-1">Cost of Inventory</p>
                                    {isLoadingKpis ? (
                                        <SpinnerCustom className='h-[48px]' />
                                    ) : (
                                        <p className="text-2xl font-semibold text-slate-800">{formatCurrency(kpiData?.data?.costOfInventory)}</p>
                                    )}
                                </div>
                                <div className="bg-white rounded-xl shadow-sm p-4">
                                    <p className="text-xs text-slate-500 font-medium mb-1">Gross Sales</p>
                                    {isLoadingKpis ? (
                                        <SpinnerCustom className='h-[48px]' />
                                    ) : (
                                        <p className="text-2xl font-semibold text-slate-800">{formatCurrency(kpiData?.data?.grossSales)}</p>
                                    )}
                                </div>
                                <div className="bg-white rounded-xl shadow-sm p-4">
                                    <p className="text-xs text-slate-500 font-medium mb-1">Gross Revenue</p>
                                    {isLoadingKpis ? (
                                        <SpinnerCustom className='h-[48px]' />
                                    ) : (
                                        <p className="text-2xl font-semibold text-slate-800">{formatCurrency(kpiData?.data?.grossRevenue)}</p>
                                    )}
                                </div>
                            </section>
                            <section className="grid grid-cols-1 lg:grid-cols-[30%_70%] gap-6">
                                {/* Item Sales */}
                                <div className="bg-white rounded-xl shadow-sm p-6">
                                    {/* Title */}
                                    <h3 className="text-xl font-semibold text-slate-800">
                                        {t('dashboard.itemSale')}
                                    </h3>
                                    {isLoadingPieData ? (<SpinnerCustom className='h-[280px]' />) : 
                                    (
                                        <>
                                            {/* Radios */}
                                            <div className="mt-3 flex items-center justify-center gap-6 text-sm">
                                                <label className="inline-flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="mode"
                                                    value="location"
                                                    checked={mode === 'location'}
                                                    onChange={() => setMode('location')}
                                                    className="accent-amber-600"
                                                />
                                                Location
                                                </label>

                                                {!effectiveLocationId && (
                                                    <label className="inline-flex items-center gap-2 cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="mode"
                                                            value="category"
                                                            checked={mode === 'category'}
                                                            onChange={() => setMode('category')}
                                                            className="accent-slate-600"
                                                        />
                                                        Category
                                                    </label>
                                                )}
                                            </div>

                                            {/* Pie chart + legend */}
                                            {locationData && locationData?.values?.length > 0 && (
                                                <div className="mt-4">
                                                <div className="mx-auto relative w-[320px] h-[320px]">
                                                    <PieChart
                                                    labels={locationData.labels}
                                                    values={locationData.values}
                                                    colors={colors}
                                                    className="relative w-[320px] h-[320px] overflow-visible"
                                                    onReady={({ update }) => setUpdatePie(() => update)}
                                                    />
                                                </div>

                                                <div>
                                                    <ul className="mt-4 space-y-2 text-sm text-slate-700 max-h-[144px] overflow-y-auto pr-1">
                                                        {legend?.map((name, i) => (
                                                            <li key={`${name}-${i}`} className="flex items-center gap-2 min-w-0">
                                                                <span
                                                                className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                                                                style={{ background: colors[i] }}
                                                                />
                                                                <span className="block text-xs leading-4 text-slate-700 break-words">
                                                                    {name}
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Shrinkage and Sales */}
                                <div>
                                    <ReportTable
                                        data={reportShrinkageData}
                                        title={t('dashboard.shrinkageAndSale')}
                                        color='#8979FF'
                                        linearFrom='rgba(137, 121, 255, 0.3)'
                                        linearTo='rgba(137, 121, 255, 0.05)'
                                        loading={isLoadingShrinkage}
                                        dateRangeDays={dateRangeDays}
                                    />
                                </div>
                            </section>

                            <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                                {/* Low Stock */}
                                <div className="bg-white rounded-xl shadow-sm p-6">
                                    <h3 className="text-xl font-semibold text-slate-800 mb-3">{t('dashboard.lowStock')}</h3>
                                    <div
                                            ref={containerLowRef}
                                            className="overflow-y-auto max-h-[360px] rounded-lg text-sm"
                                            style={{ willChange: 'scroll-position', transform: 'translateZ(0)' }}
                                            >
                                            <Table className="border-b">
                                                    <Table.Head className="sticky top-0 bg-stale-50 text-xs text-gray-900 border-b border-gray-200">
                                                        <Table.HeadCell className="text-left p-2">
                                                        {t('dashboard.location')}
                                                        </Table.HeadCell>
                                                        <Table.HeadCell className="text-left p-2">
                                                        {t('dashboard.item')}
                                                        </Table.HeadCell>
                                                        <Table.HeadCell className="px-0 py-0 text-center">
                                                        {t('dashboard.fourWeekAvgSales')}
                                                        </Table.HeadCell>
                                                        <Table.HeadCell className="px-0 py-0 text-center">
                                                        {t('dashboard.inventoryLevel')}
                                                        </Table.HeadCell>
                                                    </Table.Head>
                                                    <Table.Body>
                                                        {lowStocks?.map((r: any, index) => (
                                                            <Table.Row key={`row-${index + 1}`} className="border-b border-gray-200 text-black">
                                                                <Table.Cell className="p-2">
                                                                    <div className="truncate max-w-[150px]">
                                                                        {r.locationName}
                                                                    </div>
                                                                </Table.Cell>
                                                                <Table.Cell className="p-2">
                                                                    <div className="truncate max-w-[150px]">
                                                                        {r.itemName}
                                                                    </div>
                                                                </Table.Cell>
                                                                <Table.Cell className={`p-2 text-center`}>{r.fourWeekAvgSales}</Table.Cell>
                                                                <Table.Cell className={`p-2 text-center`}>{r.quantity}</Table.Cell>
                                                            </Table.Row>
                                                        ))}
                                                    </Table.Body>
                                            </Table>

                                            {/* infinite scroll sentinel */}
                                            <div ref={sentinelLowRef} className="h-6" />
                                            <div className="py-2 text-center text-xs text-slate-400">
                                                {isFetchingLow ? <SpinnerCustom className="h-[180px]"/> : !hasMoreLow ? '— No more data —' : ''}
                                            </div>
                                            </div>
                                </div>

                                {/* High Stock */}
                                <div className="bg-white rounded-xl shadow-sm p-6">
                                    <h3 className="text-xl font-semibold text-slate-800 mb-3">{t('dashboard.highStock')}</h3>
                                    <div
                                            ref={containerHighRef}
                                            className="overflow-y-auto max-h-[360px] rounded-lg text-sm"
                                            style={{ willChange: 'scroll-position', transform: 'translateZ(0)' }}
                                            >
                                            <Table className="w-full">
                                                <Table.Head className="sticky top-0 bg-slate-50 text-xs text-gray-900 border-b border-gray-200 z-10">
                                                <Table.HeadCell className="text-left p-2">
                                                    {t('dashboard.location')}
                                                </Table.HeadCell>
                                                <Table.HeadCell className="text-left p-2">
                                                    {t('dashboard.item')}
                                                </Table.HeadCell>
                                                <Table.HeadCell className="text-center px-0 py-0">
                                                    {t('dashboard.fourWeekAvgSales')}
                                                </Table.HeadCell>
                                                <Table.HeadCell className="text-center px-0 py-0">
                                                    {t('dashboard.inventoryLevel')}
                                                </Table.HeadCell>
                                                </Table.Head>

                                                <Table.Body>
                                                {highStocks?.map((r: any, index) => (
                                                    <Table.Row
                                                    key={`row-high-${index}`}
                                                    className="border-b border-gray-200 text-black"
                                                    >
                                                    <Table.Cell className="p-2">
                                                        <div className="truncate max-w-[150px]">
                                                            {r.locationName}
                                                        </div>
                                                    </Table.Cell>
                                                    <Table.Cell className="p-2">
                                                        <div className="truncate max-w-[150px]">
                                                            {r.itemName}
                                                        </div>
                                                    </Table.Cell>
                                                    <Table.Cell className={`p-2 text-center`}>
                                                        {r.fourWeekAvgSales}
                                                    </Table.Cell>
                                                    <Table.Cell className={`p-2 text-center`}>
                                                        {r.quantity}
                                                    </Table.Cell>
                                                    </Table.Row>
                                                ))}
                                                </Table.Body>
                                            </Table>

                                            <div ref={sentinelHighRef} className="h-6" />
                                            <div className="py-2 text-center text-xs text-slate-400">
                                                {isFetchingHigh ? <SpinnerCustom className="h-[180px]"/> : !hasMoreHigh ? '— No more data —' : ''}
                                            </div>
                                            </div>
                                </div>
                            </section>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};
export default DashboardPage;
